import { Link, useRouteError } from 'react-router';

import { ApiError } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { env } from '@/config/env';

function Shell({
  code,
  title,
  children,
}: {
  code: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <main className="flex min-h-svh flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="font-display text-6xl text-muted-foreground" data-numeric>
        {code}
      </p>
      <h1 className="text-xl font-semibold">{title}</h1>
      <div className="max-w-md text-sm text-muted-foreground">{children}</div>
      <Button asChild variant="outline">
        <Link to="/">Back to dashboard</Link>
      </Button>
    </main>
  );
}

export function NotFoundPage() {
  return (
    <Shell code="404" title="Page not found">
      That route does not exist in the admin panel.
    </Shell>
  );
}

export function ForbiddenPage() {
  return (
    <Shell code="403" title="You do not have access">
      Your account is missing the permission this section requires. Ask an administrator to grant
      it.
    </Shell>
  );
}

/**
 * Router-level error boundary.
 *
 * Shows the API's own message when the failure came from the API, because
 * "Article 41 is already published" is far more useful to an editor than a
 * generic apology. Stack traces are development-only.
 */
export function RouteErrorPage() {
  const error = useRouteError();
  const apiError = error instanceof ApiError ? error : null;

  return (
    <Shell code={apiError ? String(apiError.status) : '500'} title="Something went wrong">
      <p>{apiError?.message ?? 'An unexpected error occurred while rendering this page.'}</p>
      {env.isDev && error instanceof Error && error.stack ? (
        <pre className="mt-4 max-h-64 overflow-auto rounded-md bg-muted p-3 text-left text-xs">
          {error.stack}
        </pre>
      ) : null}
    </Shell>
  );
}
